// Client/src/utils/exportProductData.js
// Generates and downloads an XLSX file from a filtered product+COA dataset.
// Accepts an optional selectedFields map — only columns whose key is true are included.

import * as XLSX from "xlsx";
import { TERPENE_KEYS, CANNABINOID_KEYS, isCOAComplete } from "./coaPanelStatus";
import { mixDisplayName } from "./mixDisplayName";

// Synthetic cannabinoids present in the COA table but outside CANNABINOID_KEYS
const SYNTHETIC_KEYS = [
    "THC_O", "HHC", "THC_B",
    "JWH_018", "AM_694", "RCS_4", "WIN_48_098", "CP_47_497",
    "JWH_019", "JWH_073", "JWH_081", "JWH_122", "JWH_210",
    "AM_2201", "JWH_203", "JWH_250", "RCS_8", "AKB48",
    "UR_144", "XLR11", "PB_22", "5F_PB_22", "5F_AKB_48",
    "HU_210", "AB_FUBINACA", "AB_PINACA", "MDMB_CHMICA",
    "5F_MDMB_PICA", "4F_MDMB_BINACA", "5F_CUMYL_PINACA",
];

const safeNum = (v) => parseFloat(v) || 0;
const calcTotalTHC = (c) => safeNum(c.THCa) * 0.877 + safeNum(c.delta_9_THC);
const calcTotalTerps = (c) => TERPENE_KEYS.reduce((s, k) => s + safeNum(c[k]), 0);
const calcTotalCannabinoids = (c) => CANNABINOID_KEYS.reduce((s, k) => s + safeNum(c[k]), 0);

/**
 * exportProductData
 * @param {Array}  products       - Filtered product+COA rows from /products/export
 * @param {string} licenseName    - Used in the filename
 * @param {Object} selectedFields - Map of field keys → boolean (from ExportModal).
 *                                  If null, all fields are included (backward compat).
 */
export function exportProductData(products, licenseName = "export", selectedFields = null) {
    // If no selection map provided, include everything
    const sel = selectedFields ?? { _all: true };
    const inc = (key) => sel._all || sel[key] === true;

    const rows = products.map((p) => {
        const totalTerps = calcTotalTerps(p);
        const totalCann  = calcTotalCannabinoids(p);
        const tpRatio    = totalCann > 0 ? totalTerps / totalCann : 0;

        const row = {};

        // ── Product fields ────────────────────────────────────────────────────
        if (inc("product_name"))  row["Product Name"]  = mixDisplayName(p.product_name, p.product_type, p.created_at) ?? "";
        if (inc("sku"))           row["SKU"]            = p.sku ?? "";
        if (inc("product_type"))  row["Type"]           = p.product_type ?? "";
        if (inc("color"))         row["Color"]          = p.color ?? "";
        if (inc("published"))     row["Published"]      = p.published ? "Yes" : "No";
        if (inc("quantity"))      row["Quantity"]       = p.quantity ?? "";
        if (inc("weight_unit"))   row["Unit"]           = p.weight_unit ?? "";
        if (inc("price"))         row["Price"]          = p.price ?? "";
        if (inc("upload_date"))   row["Upload Date"]    = p.upload_date
                                                            ? new Date(p.upload_date).toLocaleDateString()
                                                            : "";

        // ── Computed values ───────────────────────────────────────────────────
        if (inc("total_thc"))      row["Total THC (%)"]      = +calcTotalTHC(p).toFixed(3);
        if (inc("total_terps"))    row["Total Terpenes (%)"] = +totalTerps.toFixed(3);
        if (inc("tp_ratio"))       row["T/P Ratio"]          = +tpRatio.toFixed(3);
        if (inc("panel_complete")) row["Panel Complete"]     = isCOAComplete(p) ? "Yes" : "No";

        // ── COA metadata ──────────────────────────────────────────────────────
        if (inc("lab_name"))                           row["Lab Name"]                    = p.lab_name ?? "";
        if (inc("lab_state"))                          row["Lab State"]                   = p.lab_state ?? "";
        if (inc("lab_license_number"))                 row["Lab License Number"]          = p.lab_license_number ?? "";
        if (inc("date_collected"))                     row["Date Collected"]              = p.date_collected ?? "";
        if (inc("date_received"))                      row["Date Received"]               = p.date_received ?? "";
        if (inc("date_completed"))                     row["Date Completed"]              = p.date_completed ?? "";
        if (inc("sample_id"))                          row["Sample ID"]                   = p.sample_id ?? "";
        if (inc("batch_id"))                           row["Batch ID"]                    = p.batch_id ?? "";
        if (inc("batch_metrc_id"))                     row["Batch METRC ID"]              = p.batch_metrc_id ?? "";
        if (inc("sample_metrc_id"))                    row["Sample METRC ID"]             = p.sample_metrc_id ?? "";
        if (inc("batch_biotrack_id"))                  row["Batch BioTrack ID"]           = p.batch_biotrack_id ?? "";
        if (inc("sample_biotrack_id"))                 row["Sample BioTrack ID"]          = p.sample_biotrack_id ?? "";
        if (inc("cultivator_producer_name"))           row["Cultivator/Producer"]         = p.cultivator_producer_name ?? "";
        if (inc("cultivator_producer_license_number")) row["Cultivator License"]          = p.cultivator_producer_license_number ?? "";
        if (inc("distributor_name"))                   row["Distributor"]                 = p.distributor_name ?? "";
        if (inc("distributor_license_number"))         row["Distributor License"]         = p.distributor_license_number ?? "";

        // ── Safety panels ─────────────────────────────────────────────────────
        if (inc("microbials"))       row["Microbials"]            = p.microbials ?? "";
        if (inc("pesticides"))       row["Pesticides"]            = p.pesticides ?? "";
        if (inc("heavy_metals"))     row["Heavy Metals"]          = p.heavy_metals ?? "";
        if (inc("mycotoxins"))       row["Mycotoxins"]            = p.mycotoxins ?? "";
        if (inc("foreign_matter"))   row["Foreign Matter"]        = p.foreign_matter ?? "";
        if (inc("moisture_content")) row["Moisture Content (%)"]  = p.moisture_content ?? "";

        // ── Cannabinoids (group toggle) ───────────────────────────────────────
        if (inc("cannabinoids")) {
            for (const k of CANNABINOID_KEYS) row[k] = p[k] ?? "";
            for (const k of SYNTHETIC_KEYS)   row[k] = p[k] ?? "";
        }

        // ── Terpenes (group toggle) ───────────────────────────────────────────
        if (inc("terpenes")) {
            for (const k of TERPENE_KEYS) row[k] = p[k] ?? "";
        }

        return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const date = new Date().toISOString().slice(0, 10);
    const safeName = (licenseName || "export").replace(/[/\\?%*:|"<>]/g, "_");
    XLSX.writeFile(wb, `${safeName}_product_export_${date}.xlsx`);
}
